import React, { useState, useEffect } from 'react';
import { WbSunny, DarkMode, Notifications, NotificationsOff } from '@mui/icons-material';
import { registerForPush, unregisterPush, isPushSubscribed } from '../utils/push';
import { snackbar } from '../utils/showMessage';
 
const Settings = ({ settings, updateSettings }) => {
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushLoading, setPushLoading] = useState(false);
    const [isAppInstalled, setIsAppInstalled] = useState(false);

    const getNotificationGuide = () => {
        if (typeof navigator === 'undefined') {
            return 'لطفا اعلان‌ها را در تنظیمات مرورگر فعال کنید.';
        }
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes('edg')) {
            return 'Edge: Settings > Cookies and site permissions > Notifications > Allow این سایت.';
        }
        if (ua.includes('chrome') && !ua.includes('edg') && !ua.includes('opr') && !ua.includes('brave')) {
            return 'Chrome: Site settings > Notifications > Allow این سایت.';
        }
        if (ua.includes('brave')) {
            return 'Brave: Site settings > Notifications > Allow این سایت.';
        }
        if (ua.includes('opr') || ua.includes('opera')) {
            return 'Opera: Site settings > Notifications > Allow این سایت.';
        }
        if (ua.includes('firefox')) {
            return 'Firefox: Preferences > Privacy & Security > Permissions > Notifications.';
        }
        if (ua.includes('safari')) {
            return 'Safari: Settings > Websites > Notifications > Allow این سایت.';
        }
        return 'لطفا اعلان‌ها را در تنظیمات مرورگر فعال کنید.';
    };

    useEffect(() => {
        checkPushStatus();
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const checkDisplayMode = () => {
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
            setIsAppInstalled(isStandalone);
        };
        const mediaQuery = window.matchMedia('(display-mode: standalone)');
        const handleChange = (event) => {
            const isStandalone = event.matches || window.navigator.standalone === true;
            setIsAppInstalled(isStandalone);
        };
        checkDisplayMode();
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
        } else {
            mediaQuery.addListener(handleChange);
        }
        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleChange);
            } else {
                mediaQuery.removeListener(handleChange);
            }
        };
    }, []);

    const checkPushStatus = async () => {
        const isSubscribed = await isPushSubscribed();
        setPushEnabled(isSubscribed);
    };

    const handlePushToggle = async () => {
        setPushLoading(true);
        try {
            if (pushEnabled) {
                await unregisterPush();
                setPushEnabled(false);
                snackbar('اعلان‌های فشاری غیرفعال شد');
            } else {
                const subscription = await registerForPush();
                if (subscription) {
                    setPushEnabled(true);
                    snackbar('اعلان‌های فشاری فعال شد');
                } else {
                    snackbar(getNotificationGuide());
                }
            }
        } catch (error) {
            console.error('Push toggle error:', error);
            snackbar('خطا در تغییر وضعیت اعلان‌ها');
        } finally {
            setPushLoading(false);
        }
    };

    const handleOpenInstallPrompt = () => {
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new Event('open-install-prompt'));
    };

    const colorSchemes = [
        { id: 'btn-color-blue', title: '', label: 'BL', color: 'bg-blue' },
        { id: 'btn-color-indigo', title: 'theme-indigo', label: 'IN', color: 'bg-indigo' },
        { id: 'btn-color-purple', title: 'theme-purple', label: 'PL', color: 'bg-purple' },
        { id: 'btn-color-pink', title: 'theme-pink', label: 'PK', color: 'bg-pink' },
        { id: 'btn-color-red', title: 'theme-red', label: 'RD', color: 'bg-red' },
        { id: 'btn-color-orange', title: 'theme-orange', label: 'OG', color: 'bg-orange' },
        { id: 'btn-color-yellow', title: 'theme-yellow', label: 'YL', color: 'bg-yellow' },
        { id: 'btn-color-green', title: 'theme-green', label: 'GN', color: 'bg-green' },
        { id: 'btn-color-teal', title: 'theme-teal', label: 'TL', color: 'bg-teal' },
        { id: 'btn-color-cyan', title: 'theme-cyan', label: 'CN', color: 'bg-cyan' },
    ];

    return (
        <div className="main-container container pb-4">
            <div className="row mb-3">
                <div className="col-12">
                    <h6 className="mb-3">اعلان‌های </h6>
                </div>
                <div className="col-12">
                    <button
                        className={`btn w-100 ${pushEnabled ? 'btn-success' : 'btn-outline-secondary'} shadow-sm btn-rounded`}
                        onClick={handlePushToggle}
                        disabled={pushLoading}
                    >
                        {pushLoading ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                        ) : pushEnabled ? (
                            <Notifications sx={{ fontSize: 24, verticalAlign: 'middle', mr: 1 }} />
                        ) : (
                            <NotificationsOff sx={{ fontSize: 24, verticalAlign: 'middle', mr: 1 }} />
                        )}
                        {pushEnabled ? 'اعلان‌ها فعال است' : 'فعال‌سازی اعلان‌ها'}
                    </button>
                    <p className="text-muted text-center mt-2 size-12">
                        با فعال‌سازی اعلان‌ها، از آخرین اخبار و به‌روزرسانی‌ها مطلع خواهید شد
                    </p>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-12">
                    <h6 className="mb-3">وضعیت نصب اپ</h6>
                </div>
                <div className="col-12">
                    <div className={`alert ${isAppInstalled ? 'alert-success' : 'alert-warning'} mb-0`} role="status">
                        {isAppInstalled ? 'اپ روی صفحه اصلی نصب شده است.' : 'اپ هنوز نصب نشده است.'}
                    </div>
                    {!isAppInstalled && (
                        <button
                            type="button"
                            className="btn btn-primary w-100 mt-2 btn-rounded shadow-sm"
                            onClick={handleOpenInstallPrompt}
                        >
                            نمایش پنجره نصب
                        </button>
                    )}
                </div>
            </div>

            {/* <div className="row mb-3">
                <div className="col-12">
                    <h6 className="mb-3">حالت نمایش</h6>
                </div>
                <div className="col-6 d-grid">
                    <input 
                        type="radio" 
                        className="btn-check" 
                        name="layout-mode" 
                        id="btn-layout-modes-light"
                        checked={settings.themeMode === 'light'}
                        onChange={() => updateSettings({ themeMode: 'light' })}
                    />
                    <label className="btn btn-outline-primary shadow-sm btn-rounded" htmlFor="btn-layout-modes-light">
                        <WbSunny sx={{ fontSize: 24, mb: 1 }} className="d-block mx-auto" />حالت روشن
                    </label>
                </div>
                <div className="col-6 d-grid">
                    <input 
                        type="radio" 
                        className="btn-check" 
                        name="layout-mode" 
                        id="btn-layout-modes-dark"
                        checked={settings.themeMode === 'dark'}
                        onChange={() => updateSettings({ themeMode: 'dark' })}
                    />
                    <label className="btn btn-outline-dark shadow-sm bg-dark text-white btn-rounded" htmlFor="btn-layout-modes-dark">
                        <DarkMode sx={{ fontSize: 24, mb: 1 }} className="d-block mx-auto" />حالت تاریک
                    </label>
                </div>
            </div> */}

            <div className="row mb-3">
                <div className="col-12">
                    <h6 className="mb-3">طرح رنگی</h6>
                </div>
                <div className="col-12">
                    {colorSchemes.map((scheme) => (
                        <React.Fragment key={scheme.id}>
                            <input
                                type="radio"
                                className="btn-check mb-2"
                                name="color-scheme"
                                id={scheme.id}
                                checked={settings.colorScheme === scheme.title}
                                onChange={() => updateSettings({ colorScheme: scheme.title })}
                            />
                            <label className={`btn ${scheme.color} shadow-sm avatar avatar-44 p-0 mb-3 me-2 text-white`} htmlFor={scheme.id}>
                                {scheme.label}
                            </label>
                        </React.Fragment>
                    ))}
                </div>
            </div>


        </div>
    );
};

export default Settings;
